import { BoundaryEventType } from '@flowx/designer/shared/entities/boundary-event/boundary-event.common'
import {
  ActivityNode,
  EventNodes,
  GatewayNodes,
  NodeType,
} from '@flowx/designer/shared/entities/node.types'

import {
  ActivityNodeStyles,
  ActivityNodeTypeIcons,
  BoundaryEventShapes,
  BoundaryEventTypeIcons,
  EventNodeShapes,
  EventNodeTypeIcons,
  GatewayNodeShapes,
  GatewayNodeTypeIcons,
  ProcessNodeShapes,
} from '../../bpmn-graph.constants'

export enum BpmnNodeKind {
  ACTIVITY = 'activity',
  GATEWAY = 'gateway',
  EVENT = 'event',
  BOUNDARY = 'boundary',
}

export interface BpmnNodeVisuals {
  kind: BpmnNodeKind
  width: number
  height: number
  /** Full node body as an SVG string (gateways/events); activities render a CSS rounded rect instead */
  shape: string | null
  /** Type icon as an SVG string, drawn on top of / inside the shape */
  icon: string | null
}

export const ACTIVITY_DIMENSIONS = {
  width: ActivityNodeStyles.width,
  height: ActivityNodeStyles.height,
}
export const EVENT_DIMENSIONS = { width: 40, height: 40 }
export const BOUNDARY_DIMENSIONS = { width: 32, height: 32 }

const activityIcons = new Map<NodeType, string>([
  [ActivityNode.USER_TASK, ActivityNodeTypeIcons.USER],
  [ActivityNode.TASK, ActivityNodeTypeIcons.COG],
  [ActivityNode.SUBPROCESS_RUN, ActivityNodeTypeIcons.BOX_PLUS_THICK],
  [ActivityNode.SUBPROCESS_EMBED, ActivityNodeTypeIcons.BOX_PLUS],
  [ActivityNode.MESSAGE_EVENT_SEND, ActivityNodeTypeIcons.ENVELOPE_FULL],
  [ActivityNode.MESSAGE_EVENT_RECEIVE, ActivityNodeTypeIcons.ENVELOPE_EMPTY],
])

const gatewayIcons = new Map<NodeType, string>([
  [GatewayNodes.GATEWAY_EXCLUSIVE, GatewayNodeTypeIcons.EXCLUSIVE],
  [GatewayNodes.GATEWAY_PARALLEL, GatewayNodeTypeIcons.PARALLEL],
])

const eventShapes = new Map<NodeType, string>([
  [EventNodes.START_EVENT, ProcessNodeShapes.CIRCLE_SLIM],
  [EventNodes.END_EVENT, ProcessNodeShapes.CIRCLE_THICK],
  [EventNodes.START_MESSAGE_CATCH_EVENT, EventNodeShapes.CIRCLE_SINGLE],
  [EventNodes.INTERMEDIATE_MESSAGE_CATCH_EVENT, EventNodeShapes.CIRCLE_DOUBLE],
  [EventNodes.INTERMEDIATE_MESSAGE_THROW_EVENT, EventNodeShapes.CIRCLE_DOUBLE],
  [EventNodes.START_MILESTONE, EventNodeShapes.CIRCLE_DOUBLE],
  [EventNodes.END_MILESTONE, EventNodeShapes.CIRCLE_DOUBLE],
  [EventNodes.START_TIMER_EVENT, EventNodeShapes.CIRCLE_SINGLE],
  [EventNodes.INTERMEDIATE_TIMER_EVENT, EventNodeShapes.CIRCLE_DOUBLE],
])

const eventIcons = new Map<NodeType, string>([
  [EventNodes.START_MESSAGE_CATCH_EVENT, EventNodeTypeIcons.ENVELOPE_EMPTY],
  [EventNodes.INTERMEDIATE_MESSAGE_CATCH_EVENT, EventNodeTypeIcons.ENVELOPE_EMPTY],
  [EventNodes.INTERMEDIATE_MESSAGE_THROW_EVENT, EventNodeTypeIcons.ENVELOPE_FULL],
  [EventNodes.START_TIMER_EVENT, EventNodeTypeIcons.CLOCK],
  [EventNodes.INTERMEDIATE_TIMER_EVENT, EventNodeTypeIcons.CLOCK],
  [EventNodes.START_MILESTONE, EventNodeTypeIcons.PLAY],
  [EventNodes.END_MILESTONE, EventNodeTypeIcons.STOP],
])

const boundaryShapes = new Map<BoundaryEventType, string>([
  [BoundaryEventType.MESSAGE_THROW, BoundaryEventShapes.NON_INTERRUPTING],
  [BoundaryEventType.MESSAGE_CATCH_INTERRUPTING, BoundaryEventShapes.INTERRUPTING],
  [BoundaryEventType.MESSAGE_CATCH_NON_INTERRUPTING, BoundaryEventShapes.NON_INTERRUPTING],
  [BoundaryEventType.TIMER_INTERRUPTING, BoundaryEventShapes.INTERRUPTING],
  [BoundaryEventType.TIMER_NON_INTERRUPTING, BoundaryEventShapes.NON_INTERRUPTING],
  [BoundaryEventType.ERROR_CATCH_INTERRUPTING, BoundaryEventShapes.INTERRUPTING],
])

const boundaryIcons = new Map<BoundaryEventType, string>([
  [BoundaryEventType.MESSAGE_THROW, BoundaryEventTypeIcons.ENVELOPE_FULL],
  [BoundaryEventType.MESSAGE_CATCH_INTERRUPTING, BoundaryEventTypeIcons.ENVELOPE_EMPTY],
  [BoundaryEventType.MESSAGE_CATCH_NON_INTERRUPTING, BoundaryEventTypeIcons.ENVELOPE_EMPTY],
  [BoundaryEventType.TIMER_INTERRUPTING, BoundaryEventTypeIcons.CLOCK],
  [BoundaryEventType.TIMER_NON_INTERRUPTING, BoundaryEventTypeIcons.CLOCK],
  [BoundaryEventType.ERROR_CATCH_INTERRUPTING, BoundaryEventTypeIcons.LIGHTNING],
])

const gatewayTypes: NodeType[] = [GatewayNodes.GATEWAY_EXCLUSIVE, GatewayNodes.GATEWAY_PARALLEL]

export function getNodeVisuals(type: NodeType | BoundaryEventType): BpmnNodeVisuals {
  if (activityIcons.has(type as NodeType)) {
    return {
      kind: BpmnNodeKind.ACTIVITY,
      ...ACTIVITY_DIMENSIONS,
      shape: null,
      icon: activityIcons.get(type as NodeType),
    }
  }

  if (gatewayTypes.includes(type as NodeType)) {
    return {
      kind: BpmnNodeKind.GATEWAY,
      ...EVENT_DIMENSIONS,
      shape: GatewayNodeShapes.DIAMOND,
      icon: gatewayIcons.get(type as NodeType),
    }
  }

  if (eventShapes.has(type as NodeType)) {
    return {
      kind: BpmnNodeKind.EVENT,
      ...EVENT_DIMENSIONS,
      shape: eventShapes.get(type as NodeType),
      icon: eventIcons.get(type as NodeType) ?? null,
    }
  }

  return {
    kind: BpmnNodeKind.BOUNDARY,
    ...BOUNDARY_DIMENSIONS,
    shape: boundaryShapes.get(type as BoundaryEventType) ?? null,
    icon: boundaryIcons.get(type as BoundaryEventType) ?? null,
  }
}

export function getNodeDimensions(type: NodeType | BoundaryEventType): {
  width: number
  height: number
} {
  const { width, height } = getNodeVisuals(type)

  return { width, height }
}

const SVG_FILL_PATTERN = /fill="#[0-9a-fA-F]{3,8}"/g

/**
 * Error/disabled recolor for SVG shapes: the first fill is the shape's
 * base/background, every other fill is the ring/outline color.
 */
export function recolorShapeSvg(svg: string, primary: string, background: string): string {
  let isFirstFill = true

  return svg.replace(SVG_FILL_PATTERN, () => {
    const fill = isFirstFill ? background : primary
    isFirstFill = false

    return `fill="${fill}"`
  })
}

/** Single-color recolor for type/utility icons */
export function recolorIconSvg(svg: string, color: string): string {
  return svg.replace(SVG_FILL_PATTERN, `fill="${color}"`)
}

/** Inject width/height onto a bare `<svg>` that declares neither. */
export function sizedIconSvg(svg: string, size: number): string {
  return svg.includes('width=')
    ? svg
    : svg.replace('<svg ', `<svg width="${size}" height="${size}" `)
}
